import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CircleCheck, CircleX, Copy, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { LOCALE_NAMES } from '@zt/i18n';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { useStoreChoice } from '../lib/store-choice';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  BankAccountRow,
  PaymentEvent,
  PaymentRow,
  PaymentSettingsRow,
  PaymentSettingsView,
  ReadinessCheck,
  ReconcileDetail,
  ReconcileRun,
  Store,
} from '../lib/types';
import { goLiveTarget, providerChoice } from '../lib/payment-golive';

export const paymentTone: Record<string, Tone> = {
  pending: 'amber',
  requires_action: 'amber',
  authorized: 'pine',
  captured: 'moss',
  failed: 'rose',
  cancelled: 'neutral',
  expired: 'neutral',
};
const connTone: Record<string, Tone> = {
  NOT_CONFIGURED: 'slate',
  SANDBOX: 'amber',
  LIVE: 'moss',
  ERROR: 'rose',
  DISABLED: 'neutral',
};

/** Mağaza seçimi: üst çubuktaki çalışılan mağazayla ortak (F5B-21). */
export { useStoreChoice };

/** TL kart sağlayıcıları (Sipay F7-18); Stripe ve PayPal yurt dışı ödemeler bölümünde (F7-16). */
const DOMESTIC = ['iyzico', 'paytr', 'sipay'];
/** Sağlayıcı olay kaydı olan çevrim içi ödemeler. */
const ONLINE = ['iyzico', 'paytr', 'stripe', 'paypal', 'sipay'];
/** Kartla döviz tahsilatında seçilebilen para birimleri (sağlayıcıya göre). */
const FX_CHOICES: Record<string, readonly string[]> = {
  iyzico: ['EUR', 'USD', 'GBP'],
  paytr: ['EUR', 'USD', 'GBP'],
  sipay: ['EUR', 'USD'],
  stripe: ['EUR', 'USD', 'GBP', 'AED', 'SAR', 'AZN'],
};
type LiveSlot = 'card' | 'intl' | 'paypal';

export function StorePicker({
  stores,
  storeId,
  onChange,
}: {
  stores: Store[];
  storeId: string;
  onChange: (id: string) => void;
}) {
  const t = useT();
  if (stores.length < 2) return null;
  return (
    <Field label={t('admin.payments.store')} className="max-w-xs">
      {(p) => (
        <Select {...p} value={storeId} onChange={(e) => onChange(e.target.value)}>
          {stores.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

const readinessLink: Record<string, string> = {
  seller_info: '/isletme',
  domain: '/magazalar',
  products: '/urunler',
  shipping: '/kargo',
  payment_methods: '/odeme-ayarlari',
  card_live: '/odeme-ayarlari',
  etbis: '/isletme',
  legal_published: '/yasal-metinler',
  legal_reviewed: '/yasal-metinler',
  legal_languages: '/yasal-metinler',
};

/** Madde ayrıntısı: yasal metin türü, "dil:tür" (dil sürümü eksik; F7-26) veya satıcı alanı. */
function readinessDetail(t: ReturnType<typeof useT>, key: string, d: string): string {
  if (key === 'legal_languages') {
    const [locale = '', kind = ''] = d.split(':');
    return `${LOCALE_NAMES[locale as keyof typeof LOCALE_NAMES] ?? locale}: ${t(`admin.legal.kinds.${kind}`)}`;
  }
  if (key.startsWith('legal')) return t(`admin.legal.kinds.${d}`);
  if (key === 'seller_info') return t(`admin.readiness.sellerFields.${d}`);
  return d;
}

/** Canlıya hazırlık listesi: her madde gerçek veriden hesaplanır; eksikse ilgili ekrana bağlantı verir. */
export function ReadinessList({ storeId }: { storeId: string }) {
  const t = useT();
  const q = useQuery({
    queryKey: ['readiness', storeId],
    queryFn: () => api.get<ReadinessCheck[]>(`/v1/admin/stores/${storeId}/readiness`),
    enabled: !!storeId,
  });
  if (!q.data) return <Spinner label={t('common.loading')} />;
  const done = q.data.filter((c) => c.ok).length;
  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-2">{t('admin.readiness.progress', { done, total: q.data.length })}</p>
      <ul className="divide-y divide-line rounded-md border border-line bg-surface">
        {q.data.map((c) => (
          <li key={c.key} className="flex flex-wrap items-start gap-3 px-4 py-3">
            {c.ok ? (
              <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-moss" aria-hidden />
            ) : (
              <CircleX className="mt-0.5 h-5 w-5 shrink-0 text-amber" aria-hidden />
            )}
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                <span className="sr-only">
                  {c.ok ? t('admin.readiness.ok') : t('admin.readiness.missing')}:{' '}
                </span>
                {t(`admin.readiness.items.${c.key}`)}
                {!c.required && <span className="font-normal text-ink-3"> ({t('common.optional')})</span>}
              </p>
              {!c.ok && <p className="text-sm text-ink-2">{t(`admin.readiness.hints.${c.key}`)}</p>}
              {!c.ok && c.detail && c.detail.length > 0 && (
                <p className="text-sm text-ink-3">
                  {c.detail.map((d) => readinessDetail(t, c.key, d)).join(', ')}
                </p>
              )}
            </div>
            {!c.ok && readinessLink[c.key] && (
              <Link to={readinessLink[c.key]!} className="text-sm text-pine hover:underline">
                {t('admin.readiness.fix')}
              </Link>
            )}
          </li>
        ))}
      </ul>
      <p className="text-sm text-ink-3">{t('admin.readiness.goLiveLocked')}</p>
    </div>
  );
}

function CopyLine({ label, value }: { label: string; value: string }) {
  const t = useT();
  return (
    <div className="space-y-1">
      <p className="text-sm text-ink-3">{label}</p>
      <div className="flex items-center gap-2">
        <code className="tabular block flex-1 break-all rounded-sm border border-line bg-surface-2 px-3 py-1.5 text-sm">
          {value}
        </code>
        <Button
          size="icon"
          aria-label={`${t('common.copy')}: ${label}`}
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            toast.success(t('common.copied'));
          }}
        >
          <Copy className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Ödeme ayarları (POS Kurulum Merkezi)

export function PaymentSettingsPage() {
  const t = useT();
  const money = useMoney();
  const { can, roleKey } = usePermissions();
  const manage = can('payments.provider.manage');
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const q = useQuery({
    queryKey: ['payment-settings', storeId],
    queryFn: () => api.get<PaymentSettingsView>(`/v1/admin/stores/${storeId}/payment-settings`),
    enabled: !!storeId,
  });
  const [draft, setDraft] = useState<Partial<PaymentSettingsRow> | null>(null);
  const [codFee, setCodFee] = useState<string | null>(null);
  const [codMax, setCodMax] = useState<string | null>(null);
  const [liveOpen, setLiveOpen] = useState(false);
  /** Canlıya geçiş penceresinin sağlayıcısı; null ise kayıtlı sağlayıcı (ADR-100). */
  const [liveProvider, setLiveProvider] = useState<string | null>(null);
  /** Canlıya alınan ödeme yuvası (F7-16): TL kartı, döviz kartı (Stripe) veya PayPal. */
  const [liveSlot, setLiveSlot] = useState<LiveSlot>('card');
  const isOwner = roleKey === 'owner';
  useEffect(() => {
    setDraft(null);
    setCodFee(null);
    setCodMax(null);
  }, [storeId]);
  const inv = [['payment-settings', storeId], ['readiness', storeId], ['setup']];
  const save = useApiMutation(
    (body: Record<string, unknown>) =>
      api.put<PaymentSettingsRow>(`/v1/admin/stores/${storeId}/payment-settings`, body, {
        ifMatch: q.data?.settings.version,
      }),
    {
      invalidate: inv,
      success: t('common.saved'),
      onSuccess: () => {
        setDraft(null);
        setCodFee(null);
        setCodMax(null);
        setLiveOpen(false);
        setLiveProvider(null);
      },
      silentFieldErrors: true,
    },
  );

  if (loading) return <Spinner label={t('common.loading')} />;
  if (!stores.length)
    return (
      <PageFrame helpKey="page.payment_settings">
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      </PageFrame>
    );
  if (!q.data) return <Spinner label={t('common.loading')} />;
  const base = q.data.settings;
  const s = { ...base, ...draft };
  const set = (patch: Partial<PaymentSettingsRow>) => setDraft({ ...draft, ...patch });
  const dirty = draft !== null || codFee !== null || codMax !== null;
  const providers = q.data.providers;
  const domestic = providers.filter((p) => DOMESTIC.includes(p.provider));
  const stripeState = providers.find((p) => p.provider === 'stripe');
  const paypalState = providers.find((p) => p.provider === 'paypal');
  const current = providers.find((p) => p.provider === base.cardProvider);
  // Sahip, test bağlantısı olmadan canlı bağlantılı sağlayıcıyla doğrudan canlıya geçebilir (F3B-06, ADR-100).
  const liveTargetKey =
    liveProvider ?? (liveSlot === 'card' ? goLiveTarget(domestic, base.cardProvider, isOwner) : null);
  const liveTarget = providers.find((p) => p.provider === liveTargetKey);
  const openGoLive = (provider: string | null, slot: LiveSlot = 'card') => {
    setLiveSlot(slot);
    setLiveProvider(provider);
    setLiveOpen(true);
  };
  const fxProvider = s.intlCardProvider ?? s.cardProvider;
  const envBadge = (env: 'sandbox' | 'live') => (
    <Badge tone={env === 'live' ? 'moss' : 'amber'}>
      {env === 'live' ? t('admin.payments.envLive') : t('admin.payments.envSandbox')}
    </Badge>
  );
  const hooksBase = `${window.location.origin}/v1/hooks`;
  const submit = () => {
    const body: Record<string, unknown> = { ...draft };
    if (codFee !== null) body.codFeeMinor = toMinor(codFee, 'TRY') ?? 0;
    if (codMax !== null) body.codMaxOrderMinor = codMax.trim() ? (toMinor(codMax, 'TRY') ?? null) : null;
    save.mutate(body);
  };
  const err = save.error && !save.error.errors.length ? save.error.title : undefined;

  return (
    <PageFrame helpKey="page.payment_settings">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />

      <Section title={t('admin.readiness.title')} description={t('admin.readiness.lead')}>
        <ReadinessList storeId={storeId} />
      </Section>

      <fieldset disabled={!manage} className="space-y-8">
        <Section
          title={t('admin.payments.cardTitle')}
          description={t('admin.payments.cardLead')}
          action={<HelpTip k="payments.card_provider" />}
        >
          <CardPaymentStatus
            state={!base.cardProvider ? 'off' : base.cardEnvironment === 'live' ? 'live' : 'test'}
            // TL kartının durumu yalnızca yurt içi sağlayıcılara bakar (Stripe/PayPal ayrı bölümde; F7-16).
            anySandbox={domestic.some((p) => p.sandbox === 'SANDBOX')}
            anyLive={domestic.some((p) => p.live === 'LIVE')}
          />
          {base.cardEnvironment === 'live' &&
            current &&
            !current.sandboxPaymentDone &&
            !current.liveRefundDone && (
              <div role="note" className="mb-5 rounded-md border border-amber/40 bg-amber/10 p-4 text-sm">
                <p className="flex items-center gap-1 font-medium">
                  {t('admin.payments.firstLiveOrderTitle')} <HelpTip k="payments.first_live_order" />
                </p>
                <p className="mt-1 text-ink-2">{t('admin.payments.firstLiveOrderLead')}</p>
              </div>
            )}
          <ul className="mb-5 divide-y divide-line rounded-md border border-line bg-surface">
            {providers.map((p) => (
              <li key={p.provider} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <p className="min-w-40 flex-1 font-medium">{t(`integrations.${p.provider}.name`)}</p>
                <span className="text-sm text-ink-3">{t('admin.payments.envSandbox')}</span>
                <Badge tone={connTone[p.sandbox]}>{t(`integrationStatus.${p.sandbox}`)}</Badge>
                <span className="text-sm text-ink-3">{t('admin.payments.envLive')}</span>
                <Badge tone={connTone[p.live]}>{t(`integrationStatus.${p.live}`)}</Badge>
              </li>
            ))}
          </ul>
          <p className="mb-4 text-sm text-ink-2">
            {t('admin.payments.keysHint')}{' '}
            <Link to="/entegrasyonlar" className="text-pine underline underline-offset-2">
              {t('admin.nav.integrations')}
            </Link>
          </p>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label={t('admin.payments.cardProvider')}
              help={<HelpTip k="payments.card_provider" />}
              error={fieldError(save.error, 'cardProvider')}
            >
              {(p) => (
                <Select
                  {...p}
                  value={s.cardProvider ?? ''}
                  onChange={(e) => {
                    const v = (e.target.value || null) as PaymentSettingsRow['cardProvider'];
                    const pr = providers.find((x) => x.provider === v);
                    // Yalnızca canlı bağlantısı olan sağlayıcı (sahip): seçim canlıya geçiş penceresini açar.
                    if (pr && providerChoice(pr, s.cardEnvironment, isOwner) === 'go_live') {
                      openGoLive(pr.provider);
                      return;
                    }
                    // Ön provizyon yalnızca iyzico'da (F5B-14): başka sağlayıcıda tahsilat ödeme anında.
                    set({
                      cardProvider: v,
                      ...(v !== 'iyzico' ? { cardCapture: 'immediate' as const } : {}),
                    });
                  }}
                >
                  <option value="">{t('admin.payments.cardOff')}</option>
                  {domestic.map((pr) => (
                    <option
                      key={pr.provider}
                      value={pr.provider}
                      disabled={providerChoice(pr, s.cardEnvironment, isOwner) === 'disabled'}
                    >
                      {t(`integrations.${pr.provider}.name`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium">{t('admin.payments.environment')}</p>
                <HelpTip k="payments.environment" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={base.cardEnvironment === 'live' ? 'moss' : 'amber'}>
                  {base.cardEnvironment === 'live'
                    ? t('admin.payments.envLive')
                    : t('admin.payments.envSandbox')}
                </Badge>
                {base.cardEnvironment === 'sandbox' && goLiveTarget(domestic, base.cardProvider, isOwner) && (
                  <Button size="sm" variant="primary" onClick={() => openGoLive(null)}>
                    {t('admin.payments.goLive')}
                  </Button>
                )}
                {base.cardEnvironment === 'live' && (
                  <ConfirmButton
                    variant="secondary"
                    message={t('admin.payments.backToSandboxConfirm')}
                    onConfirm={() => save.mutateAsync({ cardEnvironment: 'sandbox', cardProvider: null })}
                  >
                    {t('admin.payments.backToSandbox')}
                  </ConfirmButton>
                )}
              </div>
              {base.cardEnvironment === 'sandbox' && (
                <p className="text-[0.8125rem] text-ink-3">{t('admin.payments.sandboxNote')}</p>
              )}
            </div>
            <Field
              label={t('admin.payments.maxInstallments')}
              help={<HelpTip k="payments.max_installments" />}
            >
              {(p) => (
                <Select
                  {...p}
                  value={String(s.maxInstallments)}
                  onChange={(e) => set({ maxInstallments: Number(e.target.value) })}
                >
                  {[1, 2, 3, 6, 9, 12].map((n) => (
                    <option key={n} value={n}>
                      {n === 1 ? t('admin.payments.singlePayment') : t('admin.payments.upTo', { count: n })}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label={t('admin.payments.cardTimeout')}
              help={<HelpTip k="payments.card_timeout" />}
              error={fieldError(save.error, 'cardTimeoutMinutes')}
            >
              {(p) => (
                <Input
                  {...p}
                  inputMode="numeric"
                  className="tabular"
                  value={String(s.cardTimeoutMinutes)}
                  onChange={(e) =>
                    set({ cardTimeoutMinutes: Number(e.target.value.replace(/\D/g, '')) || 0 })
                  }
                />
              )}
            </Field>
            {s.cardProvider === 'iyzico' ? (
              <Field
                label={t('admin.payments.cardCapture')}
                help={<HelpTip k="payments.card_capture" />}
                description={
                  s.cardCapture === 'preauth' ? t('admin.payments.cardCapturePreauthHint') : undefined
                }
                error={fieldError(save.error, 'cardCapture')}
              >
                {(p) => (
                  <Select
                    {...p}
                    value={s.cardCapture}
                    onChange={(e) =>
                      set({ cardCapture: e.target.value as PaymentSettingsRow['cardCapture'] })
                    }
                  >
                    <option value="immediate">{t('admin.payments.cardCaptures.immediate')}</option>
                    <option value="preauth">{t('admin.payments.cardCaptures.preauth')}</option>
                  </Select>
                )}
              </Field>
            ) : (
              (s.cardProvider === 'paytr' || s.cardProvider === 'sipay') && (
                <p className="text-[0.8125rem] text-ink-3 sm:col-span-2">
                  {t(
                    s.cardProvider === 'paytr'
                      ? 'admin.payments.preauthPaytr'
                      : 'admin.payments.preauthSipay',
                  )}
                </p>
              )
            )}
            {fxProvider && (
              <fieldset className="space-y-2 sm:col-span-2">
                <legend className="flex items-center gap-1.5 text-sm font-medium">
                  {t('admin.payments.cardCurrencies')} <HelpTip k="payments.card_currencies" />
                </legend>
                <p className="text-[0.8125rem] text-ink-3">
                  {t(`admin.payments.cardCurrenciesHint.${fxProvider}`)}
                </p>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {(FX_CHOICES[fxProvider] ?? []).map((c) => (
                    <label key={c} className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-pine"
                        checked={s.cardCurrencies.includes(c)}
                        onChange={(e) =>
                          set({
                            cardCurrencies: e.target.checked
                              ? [...s.cardCurrencies, c]
                              : s.cardCurrencies.filter((x) => x !== c),
                          })
                        }
                      />
                      {t('admin.payments.cardCurrencyLabel', { currency: c })}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
          </div>
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium">{t('admin.payments.notifyUrls')}</p>
              <HelpTip k="payments.notify_urls" />
            </div>
            <CopyLine label={t('admin.payments.iyzicoNotify')} value={`${hooksBase}/iyzico`} />
            <CopyLine label={t('admin.payments.paytrNotify')} value={`${hooksBase}/paytr`} />
            <CopyLine label={t('admin.payments.sipayNotify')} value={`${hooksBase}/sipay`} />
          </div>
        </Section>

        <Section
          title={t('admin.payments.intlTitle')}
          description={t('admin.payments.intlLead')}
          action={<HelpTip k="payments.intl" />}
        >
          <div role="note" className="mb-5 rounded-md border border-amber/40 bg-amber/10 p-4 text-sm">
            <p className="flex items-center gap-1 font-medium">
              {t('admin.payments.intlEntityTitle')} <HelpTip k="payments.intl_entity" />
            </p>
            <p className="mt-1 text-ink-2">{t('admin.payments.intlEntityLead')}</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              label={t('admin.payments.intlCardProvider')}
              help={<HelpTip k="payments.intl_card_provider" />}
              description={t('admin.payments.intlCardHint')}
              error={fieldError(save.error, 'intlCardProvider')}
            >
              {(p) => (
                <Select
                  {...p}
                  value={s.intlCardProvider ?? ''}
                  onChange={(e) => {
                    const v = (e.target.value || null) as PaymentSettingsRow['intlCardProvider'];
                    // Yurt içi sağlayıcının almadığı para birimleri listeden düşer (ör. AED).
                    const allowed = FX_CHOICES[v ?? s.cardProvider ?? ''] ?? [];
                    set({
                      intlCardProvider: v,
                      cardCurrencies: s.cardCurrencies.filter((c) => allowed.includes(c)),
                    });
                  }}
                >
                  <option value="">{t('admin.payments.intlCardOff')}</option>
                  <option
                    value="stripe"
                    disabled={
                      !stripeState || providerChoice(stripeState, s.intlCardEnvironment, false) === 'disabled'
                    }
                  >
                    {t('integrations.stripe.name')}
                  </option>
                </Select>
              )}
            </Field>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium">{t('admin.payments.intlCardEnvironment')}</p>
                <HelpTip k="payments.environment" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {envBadge(base.intlCardEnvironment)}
                {base.intlCardEnvironment === 'sandbox' && base.intlCardProvider && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => openGoLive(base.intlCardProvider, 'intl')}
                  >
                    {t('admin.payments.goLive')}
                  </Button>
                )}
                {base.intlCardEnvironment === 'live' && (
                  <ConfirmButton
                    variant="secondary"
                    message={t('admin.payments.backToSandboxConfirm')}
                    onConfirm={() =>
                      save.mutateAsync({ intlCardEnvironment: 'sandbox', intlCardProvider: null })
                    }
                  >
                    {t('admin.payments.backToSandbox')}
                  </ConfirmButton>
                )}
              </div>
            </div>
            <div className="sm:col-span-2">
              <ToggleRow
                label={t('admin.payments.paypal')}
                hint={t('admin.payments.paypalHint')}
                checked={s.paypalEnabled}
                disabled={
                  !s.paypalEnabled &&
                  (!paypalState || providerChoice(paypalState, s.paypalEnvironment, false) === 'disabled')
                }
                help={<HelpTip k="payments.paypal" />}
                onChange={(v) => set({ paypalEnabled: v })}
              />
            </div>
            {base.paypalEnabled && (
              <div className="space-y-1.5 sm:col-span-2 sm:ps-14">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-medium">{t('admin.payments.paypalEnvironment')}</p>
                  <HelpTip k="payments.environment" />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {envBadge(base.paypalEnvironment)}
                  {base.paypalEnvironment === 'sandbox' && (
                    <Button size="sm" variant="primary" onClick={() => openGoLive('paypal', 'paypal')}>
                      {t('admin.payments.goLive')}
                    </Button>
                  )}
                  {base.paypalEnvironment === 'live' && (
                    <ConfirmButton
                      variant="secondary"
                      message={t('admin.payments.backToSandboxConfirm')}
                      onConfirm={() =>
                        save.mutateAsync({ paypalEnvironment: 'sandbox', paypalEnabled: false })
                      }
                    >
                      {t('admin.payments.backToSandbox')}
                    </ConfirmButton>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium">{t('admin.payments.notifyUrls')}</p>
              <HelpTip k="payments.intl_notify_urls" />
            </div>
            <CopyLine label={t('admin.payments.stripeNotify')} value={`${hooksBase}/stripe`} />
            <CopyLine label={t('admin.payments.paypalNotify')} value={`${hooksBase}/paypal`} />
          </div>
        </Section>

        <Section
          title={t('admin.payments.otherTitle')}
          description={t('admin.payments.otherLead')}
          action={<HelpTip k="payments.other_methods" />}
        >
          <div className="space-y-5">
            <ToggleRow
              label={t('admin.payments.bankTransfer')}
              hint={t('admin.payments.bankTransferHint')}
              checked={s.bankTransferEnabled}
              onChange={(v) => set({ bankTransferEnabled: v })}
            />
            {s.bankTransferEnabled && (
              <div className="grid gap-5 ps-14 sm:grid-cols-2">
                <Field
                  label={t('admin.payments.bankTransferHold')}
                  help={<HelpTip k="payments.bank_transfer_hold" />}
                  error={fieldError(save.error, 'bankTransferHoldHours')}
                >
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="numeric"
                      className="tabular"
                      value={String(s.bankTransferHoldHours)}
                      onChange={(e) =>
                        set({ bankTransferHoldHours: Number(e.target.value.replace(/\D/g, '')) || 0 })
                      }
                    />
                  )}
                </Field>
              </div>
            )}
            <ToggleRow
              label={t('admin.payments.cod')}
              hint={t('admin.payments.codHint')}
              checked={s.codEnabled}
              onChange={(v) => set({ codEnabled: v })}
            />
            {s.codEnabled && (
              <div className="grid gap-5 ps-14 sm:grid-cols-2">
                <Field
                  label={t('admin.payments.codFee')}
                  help={<HelpTip k="payments.cod_fee" />}
                  error={fieldError(save.error, 'codFeeMinor')}
                >
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="decimal"
                      className="tabular"
                      value={codFee ?? minorToInput(base.codFeeMinor)}
                      onChange={(e) => setCodFee(e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label={t('admin.payments.codMax')}
                  optionalLabel={t('common.optional')}
                  help={<HelpTip k="payments.cod_max" />}
                >
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="decimal"
                      className="tabular"
                      value={codMax ?? minorToInput(base.codMaxOrderMinor)}
                      onChange={(e) => setCodMax(e.target.value)}
                    />
                  )}
                </Field>
              </div>
            )}
            <ToggleRow
              label={t('admin.payments.dealerOpenAccount')}
              hint={t('admin.payments.dealerOpenAccountHint')}
              checked={s.dealerOpenAccount}
              onChange={(v) => set({ dealerOpenAccount: v })}
            />
            <ToggleRow
              label={t('admin.payments.guestCheckout')}
              hint={t('admin.payments.guestCheckoutHint')}
              checked={s.guestCheckout}
              onChange={(v) => set({ guestCheckout: v })}
            />
          </div>
        </Section>
        {err && (
          <p role="alert" className="text-sm text-rose">
            {err}
          </p>
        )}
        {dirty && (
          <div className="zt-sticky-bottom sticky bottom-0 -mx-4 flex justify-end gap-2 border-t border-line bg-paper px-4 py-3 sm:-mx-8 sm:px-8">
            <Button
              onClick={() => {
                setDraft(null);
                setCodFee(null);
                setCodMax(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="primary" loading={save.isPending} onClick={submit}>
              {t('common.save')}
            </Button>
          </div>
        )}
      </fieldset>

      <BankAccountsSection />

      <Section title={t('admin.payments.summaryTitle')}>
        <p className="text-sm text-ink-2">
          {[
            base.cardProvider
              ? t('admin.payments.summaryCard', {
                  provider: t(`integrations.${base.cardProvider}.name`),
                  env:
                    base.cardEnvironment === 'live'
                      ? t('admin.payments.envLive')
                      : t('admin.payments.envSandbox'),
                })
              : t('admin.payments.summaryNoCard'),
            base.intlCardProvider
              ? t('admin.payments.summaryIntl', {
                  provider: t(`integrations.${base.intlCardProvider}.name`),
                  env:
                    base.intlCardEnvironment === 'live'
                      ? t('admin.payments.envLive')
                      : t('admin.payments.envSandbox'),
                })
              : null,
            base.paypalEnabled
              ? t('admin.payments.summaryPaypal', {
                  env:
                    base.paypalEnvironment === 'live'
                      ? t('admin.payments.envLive')
                      : t('admin.payments.envSandbox'),
                })
              : null,
            base.bankTransferEnabled ? t('admin.payments.bankTransfer') : null,
            base.codEnabled ? `${t('admin.payments.cod')} (+${money(base.codFeeMinor, 'TRY')})` : null,
            base.dealerOpenAccount ? t('admin.payments.dealerOpenAccount') : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </Section>

      {liveOpen && liveTarget && (
        <GoLiveDialog
          provider={liveTarget.provider}
          liveConnected={liveTarget.liveConnected}
          sandboxPaymentDone={liveTarget.sandboxPaymentDone}
          isOwner={isOwner}
          busy={save.isPending}
          error={save.error?.title}
          onClose={() => {
            setLiveOpen(false);
            setLiveProvider(null);
          }}
          onConfirm={(skipSandboxTest) =>
            // Tek istekte sağlayıcı + canlı ortam (test bağlantısı olmayan sağlayıcıyla doğrudan geçişte de).
            save.mutate({
              ...(liveSlot === 'intl'
                ? { intlCardProvider: liveTarget.provider, intlCardEnvironment: 'live' }
                : liveSlot === 'paypal'
                  ? { paypalEnabled: true, paypalEnvironment: 'live' }
                  : {
                      cardProvider: liveTarget.provider,
                      cardEnvironment: 'live',
                      ...(liveTarget.provider !== 'iyzico' ? { cardCapture: 'immediate' } : {}),
                    }),
              confirmLive: true,
              ...(skipSandboxTest ? { skipSandboxTest: true } : {}),
            })
          }
        />
      )}
    </PageFrame>
  );
}

/**
 * Vitrindeki kartla ödemenin gerçek durumu (F3B-14): kapalı / test / canlı ve sonraki adım. Entegrasyonlar
 * ekranındaki "Canlı" rozeti yalnızca anahtarların bağlantı testidir; kart ödemesinin açık olduğunu göstermez.
 */
function CardPaymentStatus({
  state,
  anySandbox,
  anyLive,
}: {
  state: 'off' | 'test' | 'live';
  anySandbox: boolean;
  anyLive: boolean;
}) {
  const t = useT();
  const next = state !== 'off' ? state : anySandbox ? 'off_ready' : anyLive ? 'off_live_only' : 'off_none';
  return (
    <div className="mb-5 space-y-1.5 rounded-md border border-line bg-surface p-4" role="status">
      <p className="flex flex-wrap items-center gap-2 font-medium">
        {t('admin.payments.cardStatus')}
        <Badge tone={state === 'live' ? 'moss' : state === 'test' ? 'amber' : 'neutral'}>
          {t(`admin.payments.cardState_${state}`)}
        </Badge>
        <HelpTip k="payments.card_status" />
      </p>
      <p className="text-sm text-ink-2">{t(`admin.payments.cardNext_${next}`)}</p>
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
  disabled,
  help,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  help?: React.ReactNode;
}) {
  const [id] = useState(() => `t-${Math.random().toString(36).slice(2)}`);
  return (
    <div className="flex items-start gap-3">
      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
      <div>
        <p className="flex items-center gap-1.5">
          <label htmlFor={id} className="font-medium">
            {label}
          </label>
          {help}
        </p>
        <p className="text-sm text-ink-2">{hint}</p>
      </div>
    </div>
  );
}

/**
 * Kartla ödemeyi canlıya alma. Test ödemesi yoksa (ADR-100) yalnızca işletme sahibi, ayrı onay kutusuyla ve iki
 * adımlı doğrulamayla geçebilir; ilk canlı siparişi kendi kartıyla küçük tutarla verip iade eder.
 */
function GoLiveDialog({
  provider,
  liveConnected,
  sandboxPaymentDone,
  isOwner,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  provider: string;
  liveConnected: boolean;
  sandboxPaymentDone: boolean;
  isOwner: boolean;
  busy: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: (skipSandboxTest: boolean) => void;
}) {
  const t = useT();
  const [ack, setAck] = useState(false);
  const [skipAck, setSkipAck] = useState(false);
  const skipping = !sandboxPaymentDone && isOwner && skipAck;
  const ready = liveConnected && (sandboxPaymentDone || skipping);
  const Item = ({ ok, text }: { ok: boolean; text: string }) => (
    <li className="flex items-start gap-2">
      {ok ? (
        <CircleCheck className="mt-0.5 h-5 w-5 text-moss" aria-hidden />
      ) : (
        <CircleX className="mt-0.5 h-5 w-5 text-rose" aria-hidden />
      )}
      <span>
        <span className="sr-only">{ok ? t('admin.readiness.ok') : t('admin.readiness.missing')}: </span>
        {text}
      </span>
    </li>
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.payments.goLiveTitle', { provider: t(`integrations.${provider}.name`) })}
      description={t('admin.payments.goLiveLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={!ready || !ack}
            onClick={() => onConfirm(skipping)}
          >
            {t('admin.payments.goLiveConfirm')}
          </Button>
          <HelpTip k="payments.go_live_confirm" />
        </>
      }
    >
      <ul className="space-y-2 text-sm">
        <Item ok={liveConnected} text={t('admin.payments.checkLiveConnection')} />
        <Item ok={sandboxPaymentDone} text={t('admin.payments.checkSandboxPayment')} />
      </ul>
      {!sandboxPaymentDone && (
        <div role="note" className="mt-4 rounded-md border border-amber/40 bg-amber/10 p-3 text-sm">
          <p className="font-medium">{t('admin.payments.noSandboxTitle')}</p>
          <p className="mt-1 text-ink-2">{t('admin.payments.noSandboxLead')}</p>
          {isOwner ? (
            <label className="mt-3 flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1 accent-[var(--zt-pine)]"
                checked={skipAck}
                disabled={!liveConnected}
                onChange={(e) => setSkipAck(e.target.checked)}
              />
              <span>{t('admin.payments.noSandboxAck')}</span>
              <HelpTip k="payments.go_live_skip_sandbox" />
            </label>
          ) : (
            <p className="mt-2 text-ink-2">{t('admin.payments.noSandboxOwnerOnly')}</p>
          )}
        </div>
      )}
      <label className="mt-4 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1 accent-[var(--zt-pine)]"
          checked={ack}
          disabled={!ready}
          onChange={(e) => setAck(e.target.checked)}
        />
        <span>{t('admin.payments.goLiveAck')}</span>
      </label>
      {error && (
        <p role="alert" className="mt-3 text-sm text-rose">
          {error}
        </p>
      )}
    </Dialog>
  );
}

function BankAccountsSection() {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('payments.provider.manage');
  const q = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<BankAccountRow[]>('/v1/admin/bank-accounts'),
  });
  const [edit, setEdit] = useState<BankAccountRow | 'new' | null>(null);
  const del = useApiMutation((id: string) => api.del(`/v1/admin/bank-accounts/${id}`), {
    invalidate: [['bank-accounts']],
    success: t('common.deleted'),
  });
  return (
    <Section
      title={t('admin.payments.bankAccounts')}
      description={t('admin.payments.bankAccountsLead')}
      action={
        manage && (
          <div className="flex items-center gap-1">
            <Button size="sm" onClick={() => setEdit('new')}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.payments.addBankAccount')}
            </Button>
            <HelpTip k="payments.bank_account" />
          </div>
        )
      }
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.length ? (
        <EmptyState
          title={t('admin.payments.noBankAccounts')}
          description={t('admin.payments.noBankAccountsLead')}
        />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
          {q.data.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {a.bankName} <span className="font-normal text-ink-3">· {a.accountHolder}</span>
                </p>
                <p className="tabular text-sm text-ink-2">
                  {a.ibanFormatted}
                  {a.currency !== 'TRY' && ` · ${a.currency}`}
                </p>
              </div>
              <Badge tone={a.status === 'active' ? 'moss' : 'neutral'}>
                {t(`admin.payments.accountStatus.${a.status}`)}
              </Badge>
              {manage && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setEdit(a)}>
                    {t('common.edit')}
                  </Button>
                  <ConfirmButton
                    message={t('admin.payments.deleteBankAccountConfirm')}
                    onConfirm={() => del.mutateAsync(a.id)}
                  >
                    {t('common.delete')}
                  </ConfirmButton>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {edit && <BankAccountDialog account={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
    </Section>
  );
}

function BankAccountDialog({ account, onClose }: { account: BankAccountRow | null; onClose: () => void }) {
  const t = useT();
  const [f, setF] = useState({
    bankName: account?.bankName ?? '',
    accountHolder: account?.accountHolder ?? '',
    iban: account?.ibanFormatted ?? '',
    status: account?.status ?? 'active',
    // Döviz hesabı (F7-13): o para birimindeki siparişlerde havale/EFT bu hesaba yapılır.
    currency: account?.currency ?? 'TRY',
  });
  const save = useApiMutation(
    () =>
      account ? api.put(`/v1/admin/bank-accounts/${account.id}`, f) : api.post('/v1/admin/bank-accounts', f),
    {
      invalidate: [['bank-accounts']],
      success: t('common.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={account ? t('admin.payments.editBankAccount') : t('admin.payments.addBankAccount')}
      description={t('admin.payments.bankAccountDialogLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.payments.bankName')} required error={fieldError(save.error, 'bankName')}>
          {(p) => (
            <Input {...p} value={f.bankName} onChange={(e) => setF({ ...f, bankName: e.target.value })} />
          )}
        </Field>
        <Field
          label={t('admin.payments.accountHolder')}
          required
          error={fieldError(save.error, 'accountHolder')}
        >
          {(p) => (
            <Input
              {...p}
              value={f.accountHolder}
              onChange={(e) => setF({ ...f, accountHolder: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.payments.iban')}
          required
          help={<HelpTip k="payments.iban" />}
          error={fieldError(save.error, 'iban')}
        >
          {(p) => (
            <Input
              {...p}
              className="tabular"
              autoComplete="off"
              value={f.iban}
              placeholder="TR00 0000 0000 0000 0000 0000 00"
              onChange={(e) => setF({ ...f, iban: e.target.value.toUpperCase() })}
            />
          )}
        </Field>
        <Field
          label={t('admin.payments.accountCurrency')}
          help={<HelpTip k="payments.bank_account_currency" />}
        >
          {(p) => (
            <Select {...p} value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
              {['TRY', 'EUR', 'USD', 'GBP', 'AED', 'SAR', 'AZN'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.payments.accountStatusLabel')}>
          {(p) => (
            <Select
              {...p}
              value={f.status}
              onChange={(e) => setF({ ...f, status: e.target.value as 'active' | 'inactive' })}
            >
              <option value="active">{t('admin.payments.accountStatus.active')}</option>
              <option value="inactive">{t('admin.payments.accountStatus.inactive')}</option>
            </Select>
          )}
        </Field>
        {save.error && !save.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {save.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------------ Ödemeler ve mutabakat

export function PaymentsPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [events, setEvents] = useState<PaymentRow | null>(null);
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (provider) params.set('provider', provider);
  const q = useQuery({
    queryKey: ['payments', params.toString()],
    queryFn: () => api.get<PaymentRow[]>(`/v1/admin/payments?${params.toString()}`),
  });
  return (
    <PageFrame helpKey="page.payments">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('admin.payments.status')}>
          {(p) => (
            <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {Object.keys(paymentTone).map((s) => (
                <option key={s} value={s}>
                  {t(`admin.payments.statuses.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.payments.provider')}>
          {(p) => (
            <Select {...p} value={provider} onChange={(e) => setProvider(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {[
                'iyzico',
                'paytr',
                'sipay',
                'stripe',
                'paypal',
                'bank_transfer',
                'cash_on_delivery',
                'open_account',
                'manual',
                'gift_card',
                'store_credit',
                'loyalty_points',
              ].map((s) => (
                <option key={s} value={s}>
                  {t(`admin.payments.providers.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.length ? (
        <EmptyState title={t('admin.payments.empty')} description={t('admin.payments.emptyLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[44rem] text-sm">
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.orders.number')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.payments.provider')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.payments.status')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.payments.amount')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.payments.refunded')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {q.data.map((p) => (
                <tr key={p.id} className="border-t border-line align-top">
                  <td className="px-3 py-2">
                    <Link
                      to="/siparisler/$id"
                      params={{ id: p.orderId }}
                      className="font-medium text-pine hover:underline"
                    >
                      {p.orderNumber}
                    </Link>
                    <p className="text-ink-3">{formatDate(p.createdAt, true)}</p>
                  </td>
                  <td className="px-3 py-2">
                    {t(`admin.payments.providers.${p.provider}`)}
                    {p.environment && (
                      <p className="text-ink-3">
                        {p.environment === 'live'
                          ? t('admin.payments.envLive')
                          : t('admin.payments.envSandbox')}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={paymentTone[p.status]}>{t(`admin.payments.statuses.${p.status}`)}</Badge>
                    {p.failureMessage && <p className="mt-1 text-ink-3">{p.failureMessage}</p>}
                  </td>
                  <td className="px-3 py-2 text-end tabular">{money(p.amountMinor, p.currency)}</td>
                  <td className="px-3 py-2 text-end tabular">
                    {p.refundedMinor ? money(p.refundedMinor, p.currency) : '—'}
                  </td>
                  <td className="px-3 py-2 text-end">
                    {ONLINE.includes(p.provider) && (
                      <Button size="sm" variant="ghost" onClick={() => setEvents(p)}>
                        {t('admin.payments.events')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ReconciliationSection />
      {events && <PaymentEventsDialog payment={events} onClose={() => setEvents(null)} />}
    </PageFrame>
  );
}

function PaymentEventsDialog({ payment, onClose }: { payment: PaymentRow; onClose: () => void }) {
  const t = useT();
  const { formatDate } = useI18n();
  const q = useQuery({
    queryKey: ['payment-events', payment.id],
    queryFn: () => api.get<PaymentEvent[]>(`/v1/admin/payments/${payment.id}/events`),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      variant="sheet"
      title={t('admin.payments.eventsTitle', { number: payment.orderNumber })}
      description={t('admin.payments.eventsLead')}
      closeLabel={t('common.close')}
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.length ? (
        <p className="text-sm text-ink-2">{t('admin.payments.noEvents')}</p>
      ) : (
        <ol className="space-y-3 text-sm">
          {q.data.map((e) => (
            <li key={e.id} className="rounded-sm border border-line px-3 py-2">
              <p className="flex flex-wrap items-center gap-2">
                <span className="text-ink-3">{formatDate(e.createdAt, true)}</span>
                <Badge tone="neutral">{t(`admin.payments.eventKinds.${e.kind}`)}</Badge>
                <Badge
                  tone={e.outcome === 'applied' ? 'moss' : e.outcome === 'rejected' ? 'rose' : 'neutral'}
                >
                  {t(`admin.payments.outcomes.${e.outcome}`)}
                </Badge>
                {e.signatureValid !== null && (
                  <Badge tone={e.signatureValid ? 'moss' : 'rose'}>
                    {e.signatureValid ? t('admin.payments.signatureOk') : t('admin.payments.signatureBad')}
                  </Badge>
                )}
              </p>
              {e.message && <p className="mt-1 text-ink-2">{e.message}</p>}
            </li>
          ))}
        </ol>
      )}
    </Dialog>
  );
}

function yesterdayTr(): string {
  const d = new Date(Date.now() + 3 * 3600 * 1000 - 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

function ReconciliationSection() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [f, setF] = useState({ provider: 'iyzico', environment: 'live', date: yesterdayTr() });
  const [open, setOpen] = useState<string | null>(null);
  const runs = useQuery({
    queryKey: ['reconciliation-runs'],
    queryFn: () => api.get<ReconcileRun[]>('/v1/admin/reconciliation-runs'),
  });
  const run = useApiMutation(() => api.post<ReconcileRun>('/v1/admin/reconciliation-runs', f), {
    invalidate: [['reconciliation-runs']],
    success: t('admin.reconcile.done'),
    onSuccess: (r) => setOpen(r.id),
    silentFieldErrors: true,
  });
  return (
    <Section
      title={t('admin.reconcile.title')}
      description={t('admin.reconcile.lead')}
      action={<HelpTip k="payments.reconcile" />}
    >
      {can('payments.payment.read') && (
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <Field label={t('admin.payments.provider')}>
            {(p) => (
              <Select {...p} value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })}>
                <option value="iyzico">iyzico</option>
                <option value="paytr">PayTR</option>
              </Select>
            )}
          </Field>
          <Field label={t('admin.payments.environment')}>
            {(p) => (
              <Select
                {...p}
                value={f.environment}
                onChange={(e) => setF({ ...f, environment: e.target.value })}
              >
                <option value="live">{t('admin.payments.envLive')}</option>
                <option value="sandbox">{t('admin.payments.envSandbox')}</option>
              </Select>
            )}
          </Field>
          <Field label={t('admin.reconcile.date')} error={fieldError(run.error, 'date')}>
            {(p) => (
              <Input
                {...p}
                type="date"
                value={f.date}
                onChange={(e) => setF({ ...f, date: e.target.value })}
              />
            )}
          </Field>
          <Button loading={run.isPending} onClick={() => run.mutate(undefined)}>
            {t('admin.reconcile.run')}
          </Button>
        </div>
      )}
      {run.error && !run.error.errors.length && (
        <p role="alert" className="mb-3 text-sm text-rose">
          {run.error.title}
        </p>
      )}
      {!runs.data ? (
        <Spinner label={t('common.loading')} />
      ) : !runs.data.length ? (
        <p className="text-sm text-ink-2">{t('admin.reconcile.empty')}</p>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface text-sm">
          {runs.data.map((r) => {
            const sm = (r.summary ?? {}) as { differences?: number };
            return (
              <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
                <span className="font-medium">
                  {t(`admin.payments.providers.${r.provider}`)} · {formatDate(r.rangeStart)}
                </span>
                <span className="text-ink-3">
                  {r.environment === 'live' ? t('admin.payments.envLive') : t('admin.payments.envSandbox')}
                </span>
                <Badge
                  tone={
                    r.status === 'ok'
                      ? 'moss'
                      : r.status === 'mismatch'
                        ? 'amber'
                        : r.status === 'error'
                          ? 'rose'
                          : 'neutral'
                  }
                >
                  {r.status === 'mismatch'
                    ? t('admin.reconcile.issues', { count: sm.differences ?? 0 })
                    : t(`admin.reconcile.statuses.${r.status}`)}
                </Badge>
                <span className="flex-1" />
                <Button size="sm" variant="ghost" onClick={() => setOpen(r.id)}>
                  {t('admin.reconcile.view')}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {open && <ReconcileDialog runId={open} onClose={() => setOpen(null)} />}
    </Section>
  );
}

function ReconcileDialog({ runId, onClose }: { runId: string; onClose: () => void }) {
  const t = useT();
  const money = useMoney();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['reconciliation-run', runId],
    queryFn: () => api.get<ReconcileDetail>(`/v1/admin/reconciliation-runs/${runId}`),
  });
  const [resolving, setResolving] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const resolve = useApiMutation(
    () => api.post(`/v1/admin/reconciliation-items/${resolving}/resolve`, { note }),
    {
      invalidate: [['reconciliation-run', runId], ['reconciliation-runs']],
      success: t('common.saved'),
      onSuccess: () => {
        setResolving(null);
        setNote('');
      },
      silentFieldErrors: true,
    },
  );
  const issues = q.data?.items ?? [];
  const sm = (q.data?.run.summary ?? {}) as { localCount?: number; remoteCount?: number };
  const amountOf = (v: Record<string, unknown> | null) =>
    v && typeof v.amountMinor === 'number' ? money(v.amountMinor, 'TRY') : '—';
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      variant="sheet"
      title={t('admin.reconcile.detailTitle')}
      description={q.data?.run.error ?? undefined}
      closeLabel={t('common.close')}
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className="space-y-4 text-sm">
          <p className="text-ink-2">
            {t('admin.reconcile.summary', {
              local: sm.localCount ?? 0,
              remote: sm.remoteCount ?? 0,
              issues: issues.length,
            })}
          </p>
          {issues.length > 0 && (
            <ul className="space-y-2">
              {issues.map((i) => (
                <li key={i.id} className="rounded-sm border border-line px-3 py-2">
                  <p className="flex flex-wrap items-center gap-2">
                    <Badge tone={i.resolvedAt ? 'neutral' : 'amber'}>
                      {t(`admin.reconcile.kinds.${i.kind}`)}
                    </Badge>
                    <span className="tabular">{i.reference ?? '—'}</span>
                  </p>
                  <p className="mt-1 text-ink-2">
                    {t('admin.reconcile.amounts', { ours: amountOf(i.local), theirs: amountOf(i.remote) })}
                  </p>
                  {i.resolvedAt ? (
                    <p className="mt-1 text-ink-3">
                      {t('admin.reconcile.resolvedNote')}: {i.note}
                    </p>
                  ) : (
                    can('payments.payment.capture') &&
                    (resolving === i.id ? (
                      <div className="mt-2 space-y-2">
                        <Field
                          label={t('admin.reconcile.resolveNote')}
                          required
                          error={fieldError(resolve.error, 'note')}
                        >
                          {(p) => (
                            <Textarea
                              {...p}
                              className="min-h-16"
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                            />
                          )}
                        </Field>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => setResolving(null)}>
                            {t('common.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            loading={resolve.isPending}
                            onClick={() => resolve.mutate(undefined)}
                          >
                            {t('admin.reconcile.resolve')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => setResolving(i.id)}>
                        {t('admin.reconcile.resolve')}
                      </Button>
                    ))
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Dialog>
  );
}
